import { animate, style, transition, trigger } from '@angular/animations';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { withLatestFrom } from 'rxjs';

import { LoadMasterDataComponent } from 'app/shared/components';
import { ProductBoxRegistration } from 'app/shared/components/product-box/models';
import { ActivitySelectorComponent } from 'app/shared/features/activity-selector';
import { Activity } from 'app/shared/features/activity-selector/models';
import { getActivityTitle } from 'app/shared/features/activity-selector/utils';
import { BatchSelectorComponent } from 'app/shared/features/batch-selector';
import { Batch } from 'app/shared/features/batch-selector/models';
import { ContainerSelectorComponent } from 'app/shared/features/container-selector';
import { Container } from 'app/shared/features/container-selector/models';
import { EmployeeSelectorComponent } from 'app/shared/features/employee-selector';
import { Employee } from 'app/shared/features/employee-selector/models';
import { LocationSelectorComponent } from 'app/shared/features/location-selector';
import { Locations } from 'app/shared/features/location-selector/models';
import { MapRowSelectorComponent } from 'app/shared/features/map-row-selector';
import { activatingBreakDialog, deactivatingBreakDialog } from 'app/state/break/break.actions';
import {
    goBackInManualFlow,
    manualFlowComponentStateUpdated,
    resetManualFlowRegistrationData,
    selectActivityInManualFlow,
    setActivityInManualFlow,
    setAmountAndProductBoxInManualFlow,
    setAmountResultInManualFlow,
    setBatchInManualFlow,
    setContainerInManualFlow,
    setEmployeeInManualFlow,
    setLocationInManualFlow,
    setProductBoxInManualFlow,
    setRowInManualFlow,
} from 'app/state/manual-flow/manual-flow.actions';
import { selectManualInputComponentState } from 'app/state/manual-flow/manual-flow.selectors';
import { ComponentState } from 'app/state/manual-flow/select-state';
import {
    monitorRowRegistrationsForActivity,
    stopMonitorRowRegistrationsForActivity,
} from 'app/state/registrations/registrations.actions';
import { resetRowSelection } from 'app/state/row-selection/row-selection.actions';

import { ManualInputAmountResultComponent } from '../manual-input-amount-result/manual-input-amount-result.component';
import { ManualInputProductBoxAmountComponent } from '../manual-input-product-box-amount/manual-input-product-box-amount.component';
import { ManualInputProductBoxComponent } from '../manual-input-product-box/manual-input-product-box.component';

@Component({
    selector: 'app-manual-input-labour-registration',
    templateUrl: './manual-input-labour-registration.component.html',
    styleUrl: './manual-input-labour-registration.component.scss',
    animations: [
        trigger('slideInOut', [
            transition(':enter', [style({ left: '100%' }), animate('350ms 50ms', style({ left: '0%' }))]),
            transition(':leave', [animate('350ms', style({ left: '100%' }))]),
        ]),
        trigger('slideInOutContainers', [
            transition(':leave', [animate('50ms 500ms', style({ left: '100%' }))]),
        ]),
    ],
    imports: [
        ActivitySelectorComponent,
        BatchSelectorComponent,
        ContainerSelectorComponent,
        EmployeeSelectorComponent,
        LoadMasterDataComponent,
        LocationSelectorComponent,
        ManualInputAmountResultComponent,
        ManualInputProductBoxAmountComponent,
        MapRowSelectorComponent,
        ManualInputProductBoxComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManualInputLabourRegistrationComponent implements OnInit, OnDestroy {
    private destroyRef = inject(DestroyRef);
    private changeDetection = inject(ChangeDetectorRef);
    private store = inject(Store);
    private actions$ = inject(Actions);

    activities = input<Activity[]>([]);

    componentState = signal<ComponentState>(ComponentState.SelectActivity);
    selectedActivity = signal<Activity | undefined>(undefined);
    batchName = signal('');

    constructor() {
        this.store.dispatch(activatingBreakDialog());
    }

    ngOnInit(): void {
        this.store.dispatch(resetRowSelection());
        this.store.dispatch(resetManualFlowRegistrationData(true));
        this.actions$
            .pipe(
                ofType(manualFlowComponentStateUpdated),
                withLatestFrom(this.store.select(selectManualInputComponentState)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(([, state]) => {
                this.onStateChanged(state);
                this.componentState.set(state);
            });
    }

    ngOnDestroy(): void {
        this.store.dispatch(stopMonitorRowRegistrationsForActivity());
        this.store.dispatch(deactivatingBreakDialog());
    }

    selectedActivityTitle = computed(() => {
        return this.selectedActivity() ? getActivityTitle(this.selectedActivity()!, this.batchName()) : '';
    });

    onActivityChange(activity: Activity): void {
        this.store.dispatch(selectActivityInManualFlow());
        // Update activity based on user selection
        this.selectedActivity.set(activity);
        this.store.dispatch(setActivityInManualFlow({ activity }));
        this.monitorRowRegistrationsForActivity(activity.number);
    }

    onRowsSelected() {
        this.store.dispatch(setRowInManualFlow());
    }

    onCloseRowOverlayRequested(): void {
        this.goBack();
    }

    goBack(): void {
        this.store.dispatch(goBackInManualFlow());
    }

    onEmployeeSelected(employee: Employee): void {
        this.store.dispatch(setEmployeeInManualFlow({ employeeNumber: employee.number }));
    }

    onAmountResultSelected(amountResult: number): void {
        this.store.dispatch(setAmountResultInManualFlow({ amountResult }));
    }

    onContainersSelected(containers: Container[]): void {
        this.store.dispatch(setContainerInManualFlow({ containerList: containers }));
    }

    onLocationSelected(location: Locations): void {
        this.store.dispatch(setLocationInManualFlow(location));
    }

    onBatchSelected(batch: Batch): void {
        this.batchName.set(batch.name);
        this.store.dispatch(setBatchInManualFlow({ batchNumber: batch.number }));
    }

    private onStateChanged(state: ComponentState): void {
        switch (state) {
            case ComponentState.SelectLocation:
            case ComponentState.CreateRegistration:
                break;
            case ComponentState.SelectActivity:
            case ComponentState.SelectBatch:
                this.batchName.set('');
                break;
            default:
                // We want Angular to detect changes as soon as there's a change in progressState for the manual flow footer
                // Otherwise, the footer will not be updated until the next change detection cycle resulting in delayed UI updates
                // This only happens when we toggle Next button in the  manual flow footer or Back button in Content Header
                this.changeDetection.detectChanges();
                break;
        }
    }

    private monitorRowRegistrationsForActivity(activityNumber: number): void {
        this.store.dispatch(monitorRowRegistrationsForActivity({ activityNumber }));
    }

    onProductBoxAmountsSelected(productBoxRegistrations: ProductBoxRegistration[]) {
        this.store.dispatch(
            setAmountAndProductBoxInManualFlow({ updatedProductBoxRegistrations: productBoxRegistrations }),
        );
    }

    onProductBoxSelected(productBoxRegistrations: ProductBoxRegistration[]) {
        this.store.dispatch(
            setProductBoxInManualFlow({ updatedProductBoxRegistrations: productBoxRegistrations }),
        );
    }

    protected readonly ComponentState = ComponentState;
}
